(() => {
  'use strict';

  const STYLE_ID = 'sacha-rewards-popup-styles';
  const SOURCE_MARKER = 'data-sacha-rewards-source';
  const DATE_RE =
    /^\d{4}[\/-]\d{1,2}[\/-]\d{1,2}$/;
  const CODE_RE =
   /\b(?:LOY|BDAY|TIER|VIP|LUML)[A-Z0-9_-]{3,}\b/i;
  const STATUS_RE =
    /^(?:Approved|Pending|Rejected|Cancelled|Canceled|Expired|Issued|Used)$/i;

  let timer;

  const clean = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  function textValues(root) {
    const values = [];

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      const value = clean(node.nodeValue);

      if (
        value &&
        !/^(?:Copy|Copied|Copy code|Copy voucher code)$/i.test(
          value
        )
      ) {
        values.push(value);
      }
    }

    return values;
  }

  function parseRows(values) {
    const headerIndex =
      values.findIndex((value) =>
        /^(?:Points expire on|Expiry)$/i.test(
          value
        )
      );

    if (headerIndex < 0) return [];

    const data = values.slice(
      headerIndex + 1
    );

    const rows = [];
    let index = 0;

    while (index < data.length) {
      while (
        index < data.length &&
        !DATE_RE.test(data[index])
      ) {
        index += 1;
      }

      if (index >= data.length) break;

      const dateText = data[index];
      let statusIndex = -1;

      for (
        let cursor = index + 2;
        cursor < data.length;
        cursor += 1
      ) {
        if (DATE_RE.test(data[cursor])) {
          break;
        }

        if (
          CODE_RE.test(data[cursor]) ||
          STATUS_RE.test(data[cursor])
        ) {
          statusIndex = cursor;
          break;
        }
      }

      if (statusIndex < 0) {
        index += 1;
        continue;
      }

      const pointsIndex =
        statusIndex - 1;

      const points =
        data[pointsIndex];

      if (
        !/^[+-]?\d+(?:,\d{3})*(?:\.\d+)?$/.test(
          points
        )
      ) {
        index += 1;
        continue;
      }

      const activity = clean(
        data
          .slice(
            index + 1,
            pointsIndex
          )
          .join(' ')
      );

      rows.push({
        dateText,
        activity,
        points,
        statusOrCode:
          data[statusIndex],
        originalExpiry:
          data[statusIndex + 1] ||
          'Never'
      });

      index = statusIndex + 2;
    }

    return rows;
  }

  function findSource(headerNode) {
    let element =
      headerNode.parentElement;

    while (
      element &&
      element !== document.body
    ) {
      const values =
        textValues(element);

      const hasHeaders =
        values.some((value) =>
          /^(?:Action|Activity)$/i.test(
            value
          )
        ) &&
        values.some((value) =>
          /^Points$/i.test(value)
        ) &&
        values.some((value) =>
          /^Status$/i.test(value)
        );

      const rows =
        parseRows(values);

      if (
        hasHeaders &&
        rows.length
      ) {
        return {
          element,
          rows
        };
      }

      element =
        element.parentElement;
    }

    return null;
  }

  function parseDate(value) {
    const match = clean(value).match(
      /^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/
    );

    if (!match) return null;

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      12
    );
  }

  function addMonths(date, months) {
    const result =
      new Date(date);

    const day =
      result.getDate();

    result.setDate(1);

    result.setMonth(
      result.getMonth() + months
    );

    const finalDay =
      new Date(
        result.getFullYear(),
        result.getMonth() + 1,
        0
      ).getDate();

    result.setDate(
      Math.min(day, finalDay)
    );

    return result;
  }

  function addDays(date, days) {
    const result =
      new Date(date);

    result.setDate(
      result.getDate() + days
    );

    return result;
  }

  function formatDate(date) {
    if (!date) return '—';

    return new Intl.DateTimeFormat(
      'en-AU',
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }
    ).format(date);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function rewardRules(
    activity,
    code
  ) {
    const text =
      clean(activity).toLowerCase();

    const amountMatch =
      text.match(
        /\$(\d+(?:\.\d{1,2})?)/
      );

    const amount = amountMatch
      ? Number(amountMatch[1])
      : 0;

    if (
      /birthday|entry|tier|luminary|promotional/.test(
        text
      ) ||
      /^(?:BDAY|TIER|VIP)/i.test(
        code
      )
    ) {
      return {
        minimumSpend: 250,
        shortExpiry: false
      };
    }

    return {
      minimumSpend:
        {
          15: 200,
          30: 300,
          45: 350,
          60: 450
        }[amount] || null,

      shortExpiry:
        /style society|goal reward/.test(
          text
        ) ||
        amount === 40
    };
  }

  function usedOnline(code) {
    if (
      !Array.isArray(
        window.sachaUsedVoucherCodes
      )
    ) {
      return false;
    }

    return window
      .sachaUsedVoucherCodes
      .some((value) =>
        clean(value)
          .toUpperCase()
          .includes(
            code.toUpperCase()
          )
      );
  }

  function voucherFromRow(row) {
    const codeMatch =
      row.statusOrCode.match(
        CODE_RE
      );

    const issuedDate =
      parseDate(row.dateText);

    if (
      !codeMatch ||
      !issuedDate
    ) {
      return null;
    }

    const code =
      codeMatch[0];

    const rules =
      rewardRules(
        row.activity,
        code
      );

    const expiryDate =
      rules.shortExpiry
        ? addDays(
            issuedDate,
            30
          )
        : addMonths(
            issuedDate,
            2
          );

    const today =
      new Date();

    today.setHours(
      0,
      0,
      0,
      0
    );

    let status =
      expiryDate < today
        ? 'Expired'
        : 'Active';

    if (usedOnline(code)) {
      status = 'Redeemed';
    }

    return {
      issued:
        formatDate(issuedDate),
      reward:
        row.activity,
      code,
      minimumSpend:
        rules.minimumSpend,
      expiry:
        formatDate(expiryDate),
      status
    };
  }

  function voucherCard(voucher) {
    const statusClass =
      voucher.status
        .toLowerCase()
        .replace(/\s+/g, '-');

    return `
      <article
        class="sacha-rh-row sacha-rh-voucher"
      >
        <div
          class="sacha-rh-cell"
          data-label="Issued"
        >
          ${escapeHtml(voucher.issued)}
        </div>

        <div
          class="sacha-rh-cell sacha-rh-reward"
          data-label="Reward"
        >
          <strong>
            ${escapeHtml(voucher.reward)}
          </strong>

          <small>
            Full Priced Items Only
          </small>

          ${
            voucher.minimumSpend
              ? `
                <small>
                  Minimum spend
                  $${voucher.minimumSpend}
                </small>
              `
              : ''
          }
        </div>

        <div
          class="sacha-rh-cell sacha-rh-code"
          data-label="Voucher code"
        >
          <span>
            ${escapeHtml(voucher.code)}
          </span>

          <button
            type="button"
            data-copy-code="${escapeHtml(
              voucher.code
            )}"
          >
            Copy
          </button>
        </div>

        <div
          class="sacha-rh-cell"
          data-label="Status"
        >
          <span
            class="sacha-rh-status sacha-rh-status--${statusClass}"
          >
            ${escapeHtml(voucher.status)}
          </span>
        </div>

        <div
          class="sacha-rh-cell"
          data-label="Expiry"
        >
          ${
            voucher.status ===
            'Used online'
              ? 'Used'
              : escapeHtml(
                  voucher.expiry
                )
          }
        </div>
      </article>
    `;
  }

  function activityCard(row) {
    const expiryDate =
      parseDate(
        row.originalExpiry
      );

    return `
      <article
        class="sacha-rh-row sacha-rh-activity"
      >
        <div
          class="sacha-rh-cell"
          data-label="Date"
        >
          ${escapeHtml(
            formatDate(
              parseDate(
                row.dateText
              )
            )
          )}
        </div>

        <div
          class="sacha-rh-cell sacha-rh-reward"
          data-label="Activity"
        >
          <strong>
            ${escapeHtml(row.activity)}
          </strong>
        </div>

        <div
          class="sacha-rh-cell"
          data-label="Points"
        >
          ${escapeHtml(row.points)}
        </div>

        <div
          class="sacha-rh-cell"
          data-label="Status"
        >
          ${escapeHtml(
            row.statusOrCode
          )}
        </div>

        <div
          class="sacha-rh-cell"
          data-label="Points expiry"
        >
          ${escapeHtml(
            expiryDate
              ? formatDate(
                  expiryDate
                )
              : '—'
          )}
        </div>
      </article>
    `;
  }

  function buildPopup(rows) {
    const vouchers = rows
      .map(voucherFromRow)
      .filter(Boolean);

    const activities =
      rows.filter(
        (row) =>
          !CODE_RE.test(
            row.statusOrCode
          )
      );

    const wrapper =
      document.createElement('div');

    wrapper.className =
      'sacha-rh';

    wrapper.innerHTML = `
      <div
        class="sacha-rh-tabs"
        role="tablist"
      >
        <button
          type="button"
          class="is-active"
          data-tab="vouchers"
        >
          Vouchers
          <span>${vouchers.length}</span>
        </button>

        <button
          type="button"
          data-tab="activity"
        >
          Points &amp; Activity
          <span>${activities.length}</span>
        </button>
      </div>

      <section
        data-panel="vouchers"
      >
        <div
          class="sacha-rh-header"
        >
          <span>Issued</span>
          <span>Reward</span>
          <span>Voucher code</span>
          <span>Status</span>
          <span>Expiry</span>
        </div>

        ${
          vouchers.length
            ? vouchers
                .map(voucherCard)
                .join('')
            : `
              <p class="sacha-rh-empty">
                No vouchers found.
              </p>
            `
        }
      </section>

      <section
        data-panel="activity"
        hidden
      >
        <div
          class="sacha-rh-header"
        >
          <span>Date</span>
          <span>Activity</span>
          <span>Points</span>
          <span>Status</span>
          <span>Points expiry</span>
        </div>

        ${
          activities.length
            ? activities
                .map(activityCard)
                .join('')
            : `
              <p class="sacha-rh-empty">
                No points activity found.
              </p>
            `
        }
      </section>
    `;
wrapper
  .querySelectorAll(
    '[data-tab]'
  )
    wrapper
      .querySelectorAll(
        '[data-tab]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const selected =
              button.dataset.tab;

            wrapper
              .querySelectorAll(
                '[data-tab]'
              )
              .forEach((tab) => {
                tab.classList.toggle(
                  'is-active',
                  tab === button
                );
              });

            wrapper
              .querySelectorAll(
                '[data-panel]'
              )
              .forEach((panel) => {
                panel.hidden =
                  panel.dataset.panel !==
                  selected;
              });
          }
        );
      });

    wrapper
      .querySelectorAll(
        '[data-copy-code]'
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          async () => {
            const code = String(
  button.dataset.copyCode || ''
)
  .replace(/\s*copy\s*$/i, '')
  .trim();

            try {
              await navigator.clipboard
                .writeText(code);
            } catch (error) {
              const input =
                document.createElement(
                  'textarea'
                );

              input.value = code;

              document.body
                .appendChild(input);

              input.select();

              document.execCommand(
                'copy'
              );

              input.remove();
            }

            button.textContent =
              'Copied';

            window.setTimeout(
              () => {
                button.textContent =
                  'Copy';
              },
              1500
            );
          }
        );
      });

    return wrapper;
  }

  function addStyles() {
    if (
      document.getElementById(
        STYLE_ID
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      STYLE_ID;

    style.textContent = `
      [${SOURCE_MARKER}] {
        display: none !important;
      }

      .sacha-rh {
        color: #222;
        font-family: inherit;
        width: 100%;
      }

      .sacha-rh [hidden] {
        display: none !important;
      }

      .sacha-rh-tabs {
        border-bottom:
          1px solid #dedad7;
        display: flex;
        gap: 26px;
        margin: 8px 0 22px;
      }

      .sacha-rh-tabs button {
        appearance: none;
        background: none;
        border: 0;
        border-bottom:
          2px solid transparent;
        color: #77706c;
        cursor: pointer;
        font: inherit;
        font-size: 13px;
        margin-bottom: -1px;
        padding: 10px 0;
      }

      .sacha-rh-tabs
      button.is-active {
        border-bottom-color: #111;
        color: #111;
      }

      .sacha-rh-tabs
      button span {
        color: #918a86;
        font-size: 10px;
        margin-left: 4px;
      }

      .sacha-rh-header,
      .sacha-rh-row {
        display: grid;
        gap: 12px;
        grid-template-columns:
          15% 29% 23% 13% 20%;
      }

      .sacha-rh-header {
        color: #77706c;
        font-size: 10px;
        font-weight: 600;
        padding: 0 14px 8px;
        text-transform: uppercase;
      }

      .sacha-rh-row {
        align-items: start;
        border:
          1px solid #dedad7;
        border-radius: 4px;
        font-size: 11px;
        line-height: 1.45;
        margin-bottom: 9px;
        padding: 13px 14px;
      }

      .sacha-rh-cell {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .sacha-rh-reward strong {
        display: block;
        font-size: 12px;
        font-weight: 600;
      }

      .sacha-rh-reward small {
        color: #77706c;
        display: block;
        font-size: 10px;
      }

      .sacha-rh-code button {
        appearance: none;
        background: none;
        border: 0;
        border-bottom:
          1px solid currentColor;
        color: #222;
        cursor: pointer;
        display: block;
        font: inherit;
        font-size: 10px;
        font-weight: 600;
        margin-top: 4px;
        padding: 0;
      }

      .sacha-rh-status {
        border:
          1px solid #c8c2be;
        border-radius: 20px;
        display: inline-block;
        font-size: 9px;
        font-weight: 600;
        padding: 4px 8px;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .sacha-rh-status--active {
        background: #dedad7;
        color: #111;
      }

      .sacha-rh-status--expired,
      .sacha-rh-status--used-online {
        background: #f3f0ee;
        color: #8e8986;
      }

      .sacha-rh-empty {
        color: #77706c;
        font-size: 12px;
        padding: 20px 0;
      }

      @media (max-width: 749px) {
        .sacha-rh-tabs {
          gap: 18px;
          margin-bottom: 14px;
        }

        .sacha-rh-tabs button {
          font-size: 11px;
        }

        .sacha-rh-header {
          display: none;
        }

        .sacha-rh-row {
          gap: 10px 12px;
          grid-template-columns:
            minmax(0, 1fr) auto;
          padding: 13px;
        }

        .sacha-rh-voucher {
          grid-template-areas:
            "reward reward"
            "code status"
            "issued expiry";
        }

        .sacha-rh-activity {
  align-items: stretch !important;
  gap: 14px 16px !important;
  grid-template-areas:
    "reward reward"
    "points status"
    "issued expiry" !important;
  grid-template-columns:
    minmax(0, 1fr)
    minmax(0, 1fr) !important;
  text-align: left !important;
}

.sacha-rh-activity
.sacha-rh-cell:nth-child(1),
.sacha-rh-activity
.sacha-rh-cell:nth-child(2),
.sacha-rh-activity
.sacha-rh-cell:nth-child(3),
.sacha-rh-activity
.sacha-rh-cell:nth-child(4),
.sacha-rh-activity
.sacha-rh-cell:nth-child(5) {
  justify-self: stretch !important;
  text-align: left !important;
}

        .sacha-rh-cell::before {
          color: #918a86;
          content:
            attr(data-label);
          display: block;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: .04em;
          margin-bottom: 2px;
          text-transform: uppercase;
        }

        .sacha-rh-voucher
        .sacha-rh-cell:nth-child(1),
        .sacha-rh-activity
        .sacha-rh-cell:nth-child(1) {
          grid-area: issued;
        }

        .sacha-rh-voucher
        .sacha-rh-cell:nth-child(2),
        .sacha-rh-activity
        .sacha-rh-cell:nth-child(2) {
          grid-area: reward;
        }

        .sacha-rh-voucher
        .sacha-rh-cell:nth-child(3) {
          grid-area: code;
        }

        .sacha-rh-voucher
        .sacha-rh-cell:nth-child(4),
        .sacha-rh-activity
        .sacha-rh-cell:nth-child(4) {
          grid-area: status;
          text-align: right;
        }

        .sacha-rh-voucher
        .sacha-rh-cell:nth-child(5),
        .sacha-rh-activity
        .sacha-rh-cell:nth-child(5) {
          grid-area: expiry;
          text-align: right;
        }

        .sacha-rh-activity
        .sacha-rh-cell:nth-child(3) {
          grid-area: points;
          text-align: right;
        }
      }
    `;
style.textContent += `
  .sacha-rh {
    --rh-border: #ddd8d4;
    --rh-muted: #746d69;
    --rh-soft: #f7f4f2;
    padding-bottom: 12px;
  }

  .sacha-rh-tabs {
    border-bottom: 1px solid var(--rh-border);
    gap: 34px;
    margin: 20px 0 22px;
    padding: 0 2px;
  }

  .sacha-rh-tabs button {
    color: var(--rh-muted);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    padding: 11px 0;
    text-transform: uppercase;
  }

  .sacha-rh-tabs button.is-active {
    border-bottom-color: #111;
    color: #111;
  }

  .sacha-rh-tabs button span {
    background: #f1eeeb;
    border-radius: 10px;
    color: #746d69;
    display: inline-block;
    font-size: 8px;
    line-height: 1;
    margin-left: 5px;
    padding: 3px 5px;
    vertical-align: 1px;
  }

  .sacha-rh [data-panel] {
    background: #fff;
    border: 1px solid var(--rh-border);
    border-radius: 8px;
    overflow: hidden;
  }

  .sacha-rh-header,
  .sacha-rh-row {
    box-sizing: border-box;
    column-gap: 18px;
    grid-template-columns:
      1fr 2.1fr 1.55fr 0.9fr 1fr;
  }

  .sacha-rh-header {
    align-items: center;
    background: var(--rh-soft);
    border-bottom: 1px solid var(--rh-border);
    color: #5f5854;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.06em;
    padding: 12px 20px;
  }

  .sacha-rh-row {
    align-items: center;
    border: 0;
    border-bottom: 1px solid var(--rh-border);
    border-radius: 0;
    color: #222;
    font-size: 11px;
    line-height: 1.4;
    margin: 0;
    min-height: 78px;
    padding: 16px 20px;
    transition: background-color 0.2s ease;
  }

  .sacha-rh-row:last-child {
    border-bottom: 0;
  }

  .sacha-rh-row:hover {
    background: #fcfbfa;
  }

  .sacha-rh-cell {
    font-size: 11px;
    line-height: 1.4;
  }

  .sacha-rh-reward strong {
    color: #171717;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    margin-bottom: 4px;
  }

  .sacha-rh-reward small {
    color: var(--rh-muted);
    font-size: 9px;
    line-height: 1.45;
  }

  .sacha-rh-code {
    align-items: center;
    display: flex;
    gap: 9px;
  }

  .sacha-rh-code > span {
    color: #111;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.015em;
  }

  .sacha-rh-code button {
    background: transparent;
    border: 0 !important;
    color: #222;
    display: inline-block;
    flex: 0 0 18px;
    font-size: 0;
    height: 18px;
    margin: 0;
    opacity: 0.75;
    padding: 0;
    position: relative;
    transition:
      opacity 0.2s ease,
      transform 0.15s ease;
    width: 18px;
  }

  .sacha-rh-code button::before,
  .sacha-rh-code button::after {
    background: #fff;
    border: 1px solid currentColor;
    border-radius: 1px;
    box-sizing: border-box;
    content: "";
    height: 9px;
    position: absolute;
    width: 8px;
  }

  .sacha-rh-code button::before {
    left: 6px;
    top: 5px;
  }

  .sacha-rh-code button::after {
    left: 3px;
    top: 2px;
  }

  .sacha-rh-code button:hover {
    opacity: 1;
  }

  .sacha-rh-code button:active {
    transform: scale(0.88);
  }

  .sacha-rh-status {
    align-items: center;
    border: 0;
    border-radius: 20px;
    display: inline-flex;
    font-size: 8px;
    font-weight: 600;
    justify-content: center;
    letter-spacing: 0.04em;
    min-width: 54px;
    padding: 6px 9px;
  }

  .sacha-rh-status--active {
    background: #eeece8;
    color: #171717;
  }

  .sacha-rh-status--expired,
  .sacha-rh-status--used-online {
    background: #f6f4f2;
    color: #8a837f;
  }

  @media (max-width: 749px) {
    .sacha-rh-tabs {
      gap: 20px;
      margin: 14px 0 16px;
    }

    .sacha-rh [data-panel] {
      background: transparent;
      border: 0;
      border-radius: 0;
      overflow: visible;
    }

    .sacha-rh-row {
      border: 1px solid var(--rh-border);
      border-radius: 7px;
      gap: 12px;
      grid-template-columns:
        minmax(0, 1fr) auto;
      margin-bottom: 9px;
      min-height: 0;
      padding: 14px;
    }

    .sacha-rh-code {
      justify-content: flex-start;
    }
  }
    @media (min-width: 750px) {
  .sacha-rh.sacha-rh
  .sacha-rh-head,
  .sacha-rh.sacha-rh
  .sacha-rh-header {
    font-size: 11px !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-row,
  .sacha-rh.sacha-rh
  .sacha-rh-cell {
    font-size: 10.5px !important;
    line-height: 1.4 !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-reward strong {
    font-size: 11.5px !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-details {
    font-size: 9.5px !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-badge {
    font-size: 9.5px !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-head > :nth-child(3),
  .sacha-rh.sacha-rh
  .sacha-rh-header > :nth-child(3) {
    text-align: center !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-code {
    align-items: center !important;
    display: flex !important;
    gap: 6px !important;
    grid-template-columns: none !important;
    justify-content: center !important;
    text-align: center !important;
    width: 100% !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-code > span {
    flex: 0 1 auto !important;
    width: auto !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-code > button {
    flex: 0 0 16px !important;
    margin: 0 !important;
    min-width: 16px !important;
    width: 16px !important;
  }
}
`;
    document.head.appendChild(
      style
    );
  }
/*
 * Final row parser and visual polish.
 */
parseRows = function (values) {
  const headerIndex =
    values.findIndex((value) =>
      /^(?:Points expire on|Expiry)$/i.test(
        value
      )
    );

  if (headerIndex < 0) return [];

  const data =
    values.slice(headerIndex + 1);

  const rows = [];
  let index = 0;

  while (index < data.length) {
    if (!DATE_RE.test(data[index])) {
      index += 1;
      continue;
    }

    if (index + 4 >= data.length) {
      break;
    }

    const points =
      data[index + 2];

    if (
      !/^[+-]?\d+(?:,\d{3})*(?:\.\d+)?$/.test(
        points
      )
    ) {
      index += 1;
      continue;
    }

    rows.push({
      dateText: data[index],
      activity: data[index + 1],
      points,
      statusOrCode: data[index + 3],
      originalExpiry: data[index + 4]
    });

    index += 5;
  }

  return rows;
};

if (
  !document.getElementById(
    'sacha-rh-final-polish'
  )
) {
  const finalStyle =
    document.createElement('style');

  finalStyle.id =
    'sacha-rh-final-polish';

  finalStyle.textContent = `
    .sacha-rh {
      --rh-black: #111111;
      --rh-gold: #b18a2e;
      --rh-beige: #f4f0ec;
      --rh-border: #ded6cf;
      --rh-muted: #746a63;
    }

    .sacha-rh .sacha-rh-tabs {
      gap: 28px !important;
      margin: 12px 0 14px !important;
    }

    .sacha-rh
    .sacha-rh-tabs button {
      font-size: 11px !important;
      letter-spacing: 0.06em !important;
      padding: 10px 0 !important;
    }

    .sacha-rh
    .sacha-rh-tabs
    button.is-active {
      border-bottom-color:
        var(--rh-gold) !important;
      color:
        var(--rh-black) !important;
    }

    .sacha-rh
    .sacha-rh-tabs
    button span {
      background:
        var(--rh-beige) !important;
      color: #8d6b20 !important;
      font-size: 9px !important;
      padding: 3px 6px !important;
    }

    .sacha-rh [data-panel] {
      background: #ffffff !important;
      border:
        1px solid
        var(--rh-border) !important;
      border-radius: 7px !important;
      overflow: hidden !important;
    }

    .sacha-rh
    .sacha-rh-header,
    .sacha-rh
    .sacha-rh-row {
      column-gap: 14px !important;
      grid-template-columns:
        1fr
        2fr
        1.65fr
        0.9fr
        1fr !important;
    }

    .sacha-rh
    .sacha-rh-header {
      background:
        var(--rh-beige) !important;
      color:
        var(--rh-black) !important;
      font-size: 11px !important;
      letter-spacing: 0.01em !important;
      padding: 10px 16px !important;
      text-transform: none !important;
    }

    .sacha-rh
    .sacha-rh-row {
      align-items: center !important;
      background: #ffffff !important;
      border: 0 !important;
      border-bottom:
        1px solid
        var(--rh-border) !important;
      border-radius: 0 !important;
      font-size: 12px !important;
      line-height: 1.35 !important;
      margin: 0 !important;
      min-height: 64px !important;
      padding: 11px 16px !important;
    }

    .sacha-rh
    .sacha-rh-row:last-child {
      border-bottom: 0 !important;
    }

    .sacha-rh
    .sacha-rh-cell {
      color:
        var(--rh-black) !important;
      font-size: 12px !important;
      line-height: 1.35 !important;
    }

    .sacha-rh
    .sacha-rh-reward strong {
      color:
        var(--rh-black) !important;
      font-size: 12px !important;
      line-height: 1.3 !important;
      margin-bottom: 3px !important;
    }

    .sacha-rh
    .sacha-rh-reward small {
      color:
        var(--rh-muted) !important;
      font-size: 10px !important;
      line-height: 1.35 !important;
    }

    .sacha-rh
    .sacha-rh-code {
      align-items: center !important;
      display: grid !important;
      gap: 7px !important;
      grid-template-columns:
        minmax(0, 1fr)
        20px !important;
      width: 100% !important;
    }

    .sacha-rh
    .sacha-rh-code > span {
      color:
        var(--rh-black) !important;
      font-size: 12px !important;
      letter-spacing: 0.01em !important;
      min-width: 0 !important;
      white-space: nowrap !important;
    }

    /*
     * Use a real SVG background so Shopify
     * cannot turn COPY into vertical text.
     */
    .sacha-rh
    .sacha-rh-code button {
      appearance: none !important;
      background-color:
        transparent !important;
      background-image:
        url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23b18a2e'%20stroke-width='1.8'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Crect%20x='9'%20y='9'%20width='10'%20height='10'%20rx='1'/%3E%3Cpath%20d='M15%209V6a1%201%200%200%200-1-1H6a1%201%200%200%200-1%201v8a1%201%200%200%200%201%201h3'/%3E%3C/svg%3E") !important;
      background-position:
        center !important;
      background-repeat:
        no-repeat !important;
      background-size:
        16px 16px !important;
      border: 0 !important;
      color:
        transparent !important;
      display: block !important;
      flex: none !important;
      font-size: 0 !important;
      height: 20px !important;
      line-height: 0 !important;
      margin: 0 !important;
      min-height: 20px !important;
      min-width: 20px !important;
      overflow: hidden !important;
      padding: 0 !important;
      text-indent: -9999px !important;
      width: 20px !important;
    }

    .sacha-rh
    .sacha-rh-code
    button::before,
    .sacha-rh
    .sacha-rh-code
    button::after {
      content: none !important;
      display: none !important;
    }

    .sacha-rh
    .sacha-rh-status {
      align-items: center !important;
      border: 0 !important;
      border-radius: 20px !important;
      display: inline-flex !important;
      font-size: 10px !important;
      font-weight: 600 !important;
      justify-content: center !important;
      letter-spacing: 0.02em !important;
      min-width: 58px !important;
      padding: 6px 10px !important;
    }

    .sacha-rh
    .sacha-rh-status--active {
      background:
        var(--rh-black) !important;
      color: #ffffff !important;
    }

    .sacha-rh
    .sacha-rh-status--expired {
      background:
        var(--rh-beige) !important;
      color:
        var(--rh-muted) !important;
    }

    .sacha-rh
    .sacha-rh-status--used-online {
      background: #ffffff !important;
      border:
        1px solid
        var(--rh-gold) !important;
      color: #89691f !important;
    }

    @media (max-width: 749px) {
      .sacha-rh
      .sacha-rh-tabs {
        gap: 16px !important;
        margin: 10px 0 14px !important;
        overflow-x: auto !important;
      }

      .sacha-rh
      .sacha-rh-tabs button {
        flex: 0 0 auto !important;
        font-size: 10px !important;
        white-space: nowrap !important;
      }

      .sacha-rh
      [data-panel] {
        background:
          transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        overflow: visible !important;
      }

      .sacha-rh
      .sacha-rh-header {
        display: none !important;
      }

      .sacha-rh
      .sacha-rh-row {
        border:
          1px solid
          var(--rh-border) !important;
        border-radius: 7px !important;
        gap: 10px 14px !important;
        grid-template-columns:
          minmax(0, 1fr)
          auto !important;
        margin-bottom: 8px !important;
        min-height: 0 !important;
        padding: 13px !important;
      }

      .sacha-rh
      .sacha-rh-voucher {
        grid-template-areas:
          "reward reward"
          "code status"
          "issued expiry" !important;
      }

      .sacha-rh
      .sacha-rh-activity {
        grid-template-areas:
          "reward reward"
          "issued points"
          "status expiry" !important;
      }

      .sacha-rh
      .sacha-rh-cell {
        font-size: 11.5px !important;
      }

      .sacha-rh
      .sacha-rh-cell::before {
        color:
          var(--rh-muted) !important;
        font-size: 9px !important;
        margin-bottom: 3px !important;
      }

      .sacha-rh
      .sacha-rh-reward strong {
        font-size: 12.5px !important;
      }

      .sacha-rh
      .sacha-rh-reward small {
        font-size: 10px !important;
      }

      .sacha-rh
      .sacha-rh-code > span {
        font-size: 11.5px !important;
        white-space: normal !important;
        word-break: break-all !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(1),
      .sacha-rh-activity
      .sacha-rh-cell:nth-child(1) {
        grid-area: issued !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(2),
      .sacha-rh-activity
      .sacha-rh-cell:nth-child(2) {
        grid-area: reward !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(3) {
        grid-area: code !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(4),
      .sacha-rh-activity
      .sacha-rh-cell:nth-child(4) {
        grid-area: status !important;
        text-align: right !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(5),
      .sacha-rh-activity
      .sacha-rh-cell:nth-child(5) {
        grid-area: expiry !important;
        text-align: right !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(3) {
        grid-area: points !important;
        text-align: right !important;
      }
    }
  `;

  document.head.appendChild(
    finalStyle
  );
}
  function render() {
    addStyles();

    const walker =
      document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT
      );

    const headers = [];
    let node;

    while (
      (node = walker.nextNode())
    ) {
      if (
        /^Points expire on$/i.test(
          clean(node.nodeValue)
        )
      ) {
        headers.push(node);
      }
    }

    headers.forEach(
      (headerNode) => {
        const found =
          findSource(headerNode);

        if (
          !found ||
          found.element.hasAttribute(
            SOURCE_MARKER
          )
        ) {
          return;
        }

        const popup =
          buildPopup(found.rows);

        found.element.parentNode
          .insertBefore(
            popup,
            found.element
          );

        found.element.setAttribute(
          SOURCE_MARKER,
          'true'
        );
      }
    );
  }
/*
 * Mobile Yotpo history fallback.
 * Yotpo removes its table headers on mobile,
 * so detect and read each original card instead.
 */
const MOBILE_MARKER =
  'data-sacha-rewards-mobile-ready';

const MOBILE_STATUS_RE =
  /^(?:Approved|Refunded|Pending|Rejected|Cancelled|Canceled|Completed|Redeemed|Expired|Issued|Used|Active|Earned|Success)$/i;

function mobileCards(root) {
  const results = [];
  const seen = new Set();

  const walker =
    document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );

  const dateNodes = [];
  let node;

  while ((node = walker.nextNode())) {
    if (
      DATE_RE.test(
        clean(node.nodeValue)
      )
    ) {
      dateNodes.push(node);
    }
  }

  dateNodes.forEach((dateNode) => {
    let element =
      dateNode.parentElement;

    while (
      element &&
      element !== root.parentElement
    ) {
      const values =
        textValues(element);

      const dates =
        values.filter((value) =>
          DATE_RE.test(value)
        );

      const code =
        values.find((value) =>
          CODE_RE.test(value)
        );

      const status =
        values.find((value) =>
          MOBILE_STATUS_RE.test(value)
        );

      const pointsValue =
        values.find((value) =>
          /^[+-]?\d+(?:,\d{3})*(?:\.\d+)?(?:\s+Points?)?$/i.test(
            value
          )
        );

      const identity =
        code || status;

      if (
        dates.length >= 1 &&
        dates.length <= 2 &&
        identity &&
        pointsValue &&
        values.length <= 14
      ) {
        if (seen.has(element)) {
          break;
        }

        const activity = values
          .filter((value) => {
            if (DATE_RE.test(value)) {
              return false;
            }

            if (CODE_RE.test(value)) {
              return false;
            }

            if (
              MOBILE_STATUS_RE.test(
                value
              )
            ) {
              return false;
            }

            if (
              value === pointsValue
            ) {
              return false;
            }

            if (
              /^(?:Never|Rewards History|Date|Action|Activity|Points|Status|Points expire on|Expiry|×|x)$/i.test(
                value
              )
            ) {
              return false;
            }

            return true;
          })
          .join(' ');

        if (!activity) {
          element =
            element.parentElement;

          continue;
        }

        results.push({
          element,
          row: {
            dateText: dates[0],
            activity,
            points:
              pointsValue.replace(
                /\s+Points?$/i,
                ''
              ),
            statusOrCode: identity,
            originalExpiry:
              values.find((value) =>
                /^Never$/i.test(value)
              ) ||
              dates[1] ||
              'Never'
          }
        });

        seen.add(element);
        break;
      }

      element =
        element.parentElement;
    }
  });

  return results;
}

function findMobileHistory(titleNode) {
  let element =
    titleNode.parentElement;

  while (
    element &&
    element !== document.body
  ) {
    const values =
      textValues(element);

    const hasDates =
      values.some((value) =>
        DATE_RE.test(value)
      );

    const hasPoints =
      values.some((value) =>
        /Points/i.test(value)
      );

    const hasIdentity =
      values.some((value) =>
        CODE_RE.test(value) ||
        MOBILE_STATUS_RE.test(value)
      );

    if (
      hasDates &&
      hasPoints &&
      hasIdentity
    ) {
      const cards =
        mobileCards(element);

      if (cards.length) {
        return {
          root: element,
          cards
        };
      }
    }

    element =
      element.parentElement;
  }

  return null;
}

function renderMobileHistory() {
  const walker =
    document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT
    );

  const titles = [];
  let node;

  while ((node = walker.nextNode())) {
    if (
      clean(node.nodeValue) ===
      'Rewards History' &&
      !node.parentElement.closest(
        '.sacha-rh'
      )
    ) {
      titles.push(node);
    }
  }

  titles.forEach((titleNode) => {
    const found =
      findMobileHistory(titleNode);

    if (
      !found ||
      found.root.hasAttribute(
        MOBILE_MARKER
      )
    ) {
      return;
    }

    const rows =
      found.cards.map(
        (card) => card.row
      );
rows.forEach((row) => {
  const activityText =
    clean(row.activity);

  const expiryMatch =
    activityText.match(
      /points expire on\s*(\d{4}[/-]\d{1,2}[/-]\d{1,2})/i
    );

  if (expiryMatch) {
    row.activity = clean(
      activityText.replace(
        expiryMatch[0],
        ''
      )
    );

    row.originalExpiry =
      expiryMatch[1];
  }
});
    const custom =
      buildPopup(rows);

    const firstCard =
      found.cards[0].element;

    firstCard.parentNode.insertBefore(
        
      custom,
      firstCard
    );
    custom.style.setProperty(
  'width',
  '100%',
  'important'
);

custom.style.setProperty(
  'min-width',
  '0',
  'important'
);

custom.style.setProperty(
  'overflow-x',
  'hidden',
  'important'
);

let sachaScrollLockNode = custom;

while (
  sachaScrollLockNode &&
  sachaScrollLockNode !== document.body
) {
  sachaScrollLockNode.style.setProperty(
    'overflow-x',
    'hidden',
    'important'
  );

  sachaScrollLockNode.style.setProperty(
    'overscroll-behavior-x',
    'none',
    'important'
  );

  sachaScrollLockNode.style.setProperty(
    'touch-action',
    'pan-y',
    'important'
  );

  sachaScrollLockNode =
    sachaScrollLockNode.parentElement;
}

document.documentElement.style.setProperty(
  'overflow-x',
  'hidden',
  'important'
);

document.body.style.setProperty(
  'overflow-x',
  'hidden',
  'important'
);
    found.cards.forEach((card) => {
      card.element.setAttribute(
        SOURCE_MARKER,
        'true'
      );
    });

    found.root.setAttribute(
      MOBILE_MARKER,
      'true'
    );
  });
}

/*
 * Keep the working desktop renderer,
 * then add the mobile renderer on phones.
 */
const desktopRender = render;

render = function () {
  desktopRender();

  if (
    window.matchMedia(
      '(max-width: 749px)'
    ).matches
  ) {
    renderMobileHistory();
  }
};
/*
 * Final mobile card redesign.
 */
if (
  !document.getElementById(
    'sacha-rh-mobile-redesign'
  )
) {
  const mobileStyle =
    document.createElement('style');

  mobileStyle.id =
    'sacha-rh-mobile-redesign';

  mobileStyle.textContent = `
    @media (max-width: 749px) {
      .sacha-rh {
        margin-top: 0 !important;
        width: 100% !important;
      }

      /*
       * Two equal tabs so neither is cut off.
       */
      .sacha-rh
      .sacha-rh-tabs {
        display: grid !important;
        gap: 0 !important;
        grid-template-columns:
          minmax(0, 1fr)
          minmax(0, 1fr) !important;
        margin: 0 0 14px !important;
        overflow: visible !important;
        width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-tabs button {
        align-items: center !important;
        display: flex !important;
        font-size: 10px !important;
        gap: 4px !important;
        justify-content: center !important;
        letter-spacing: 0.035em !important;
        min-width: 0 !important;
        padding: 11px 3px !important;
        text-align: center !important;
        white-space: nowrap !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button span {
        flex: 0 0 auto !important;
        font-size: 8px !important;
        margin: 0 !important;
      }

      /*
       * Compact mobile voucher card.
       */
      .sacha-rh
      .sacha-rh-row {
        background: #ffffff !important;
        border:
          1px solid
          var(--rh-border) !important;
        border-radius: 8px !important;
        gap: 13px 16px !important;
        margin-bottom: 10px !important;
        min-height: 0 !important;
        padding: 15px !important;
      }

      .sacha-rh
      .sacha-rh-voucher {
        grid-template-areas:
          "reward reward"
          "code status"
          "issued expiry" !important;

        grid-template-columns:
          minmax(0, 1fr)
          auto !important;
      }

      /*
       * Reward section across the top.
       */
      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(2) {
        border-bottom:
          1px solid #eee8e3 !important;
        grid-area: reward !important;
        padding-bottom: 12px !important;
        text-align: left !important;
      }

      .sacha-rh
      .sacha-rh-reward strong {
        font-size: 15px !important;
        line-height: 1.25 !important;
        margin-bottom: 5px !important;
      }

      .sacha-rh
      .sacha-rh-reward small {
        font-size: 11px !important;
        line-height: 1.4 !important;
      }

      /*
       * Voucher code: label on top,
       * code and icon together underneath.
       */
      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(3) {
        align-self: end !important;
        display: grid !important;
        gap: 4px 7px !important;
        grid-area: code !important;

        grid-template-areas:
          "code-label code-label"
          "code-value copy-icon" !important;

        grid-template-columns:
          minmax(0, max-content)
          20px !important;

        justify-self: start !important;
        width: auto !important;
      }

      .sacha-rh
      .sacha-rh-code::before {
        grid-area:
          code-label !important;
          justify-self: start !important;
text-align: left !important;
width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-code > span {
        font-size: 12.5px !important;
        grid-area:
          code-value !important;
        letter-spacing: 0.015em !important;
        line-height: 1.2 !important;
        white-space: nowrap !important;
        word-break: normal !important;
      }

      .sacha-rh
      .sacha-rh-code button {
        align-self: center !important;
        grid-area:
          copy-icon !important;
        justify-self: start !important;
      }

      /*
       * Status sits neatly beside the code.
       */
      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(4) {
        align-self: end !important;
        grid-area: status !important;
        justify-self: end !important;
        text-align: right !important;
      }

      .sacha-rh
      .sacha-rh-status {
        font-size: 10px !important;
        min-width: 58px !important;
        padding: 6px 10px !important;
      }

      /*
       * Issued and expiry form the bottom row.
       */
      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(1) {
        grid-area: issued !important;
        text-align: left !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(5) {
        grid-area: expiry !important;
        text-align: right !important;
      }

      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(1),
      .sacha-rh-voucher
      .sacha-rh-cell:nth-child(5) {
        color:
          var(--rh-black) !important;
        font-size: 11.5px !important;
      }

      /*
       * Consistent small field headings.
       */
      .sacha-rh
      .sacha-rh-cell::before {
        color:
          var(--rh-muted) !important;
        font-size: 9px !important;
        font-weight: 600 !important;
        letter-spacing: 0.045em !important;
        margin-bottom: 4px !important;
      }

      /*
       * Activity cards use the same compact style.
       */
      .sacha-rh
      .sacha-rh-activity {
        grid-template-areas:
          "reward reward"
          "issued points"
          "status expiry" !important;

        grid-template-columns:
          minmax(0, 1fr)
          auto !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(2) {
        border-bottom:
          1px solid #eee8e3 !important;
        grid-area: reward !important;
        padding-bottom: 11px !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(1) {
        grid-area: issued !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(3) {
        grid-area: points !important;
        text-align: right !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(4) {
        grid-area: status !important;
      }

      .sacha-rh-activity
      .sacha-rh-cell:nth-child(5) {
        grid-area: expiry !important;
        text-align: right !important;
      }
    }
  `;

  document.head.appendChild(
    mobileStyle
  );
}
/*
 * Mobile tab button redesign.
 */
if (
  !document.getElementById(
    'sacha-rh-mobile-tab-buttons'
  )
) {
  const tabStyle =
    document.createElement('style');

  tabStyle.id =
    'sacha-rh-mobile-tab-buttons';

  tabStyle.textContent = `
    @media (max-width: 749px) {
      .sacha-rh
      .sacha-rh-tabs {
        border: 0 !important;
        box-sizing: border-box !important;
        display: grid !important;
        gap: 8px !important;
        grid-template-columns:
          repeat(
            2,
            minmax(0, 1fr)
          ) !important;
        margin: 0 0 16px !important;
        max-width: 100% !important;
        overflow: visible !important;
        padding: 0 !important;
        width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-tabs button {
        align-items: center !important;
        appearance: none !important;
        background:
          var(--rh-beige) !important;
        border:
          1px solid
          var(--rh-gold) !important;
        border-radius: 30px !important;
        box-sizing: border-box !important;
        color:
          var(--rh-black) !important;
        display: flex !important;
        font-size: 9.5px !important;
        font-weight: 600 !important;
        gap: 4px !important;
        justify-content: center !important;
        letter-spacing: 0.015em !important;
        line-height: 1.2 !important;
        margin: 0 !important;
        max-width: 100% !important;
        min-height: 42px !important;
        min-width: 0 !important;
        overflow: hidden !important;
        padding: 9px 5px !important;
        text-align: center !important;
        white-space: nowrap !important;
        width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button.is-active {
        background: #c4a052 !important;
        border-color: #c4a052 !important;
        color: #111111 !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button span {
        background:
          rgba(255, 255, 255, 0.65) !important;
        border-radius: 10px !important;
        color: #111111 !important;
        flex: 0 0 auto !important;
        font-size: 8px !important;
        line-height: 1 !important;
        margin: 0 !important;
        padding: 3px 5px !important;
      }
    }
  `;

  document.head.appendChild(
    tabStyle
  );
}
/*
 * Lock mobile tabs to equal widths.
 */
if (
  !document.getElementById(
    'sacha-rh-mobile-tabs-fit'
  )
) {
  const tabsFitStyle =
    document.createElement('style');

  tabsFitStyle.id =
    'sacha-rh-mobile-tabs-fit';

  tabsFitStyle.textContent = `
    @media (max-width: 749px) {
      .sacha-rh
      .sacha-rh-tabs {
        background:
          var(--rh-beige) !important;
        border:
          1px solid
          var(--rh-gold) !important;
        border-radius: 30px !important;
        box-sizing: border-box !important;
        display: grid !important;
        gap: 0 !important;
        grid-template-columns:
          minmax(0, 1fr)
          minmax(0, 1fr) !important;
        margin: 0 0 16px !important;
        max-width: 100% !important;
        overflow: hidden !important;
        padding: 0 !important;
        width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-tabs button {
        align-items: center !important;
        background:
          transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        box-sizing: border-box !important;
        display: flex !important;
        float: none !important;
        font-size: 9.5px !important;
        gap: 4px !important;
        grid-column: auto !important;
        inset: auto !important;
        justify-content: center !important;
        justify-self: stretch !important;
        letter-spacing: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
        min-height: 44px !important;
        min-width: 0 !important;
        overflow: hidden !important;
        padding: 9px 4px !important;
        position: static !important;
        transform: none !important;
        white-space: normal !important;
        width: 100% !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button:first-child {
        border-radius:
          28px 0 0 28px !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button:last-child {
        border-radius:
          0 28px 28px 0 !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button.is-active {
        background:
          #c4a052 !important;
        color: #111111 !important;
      }

      .sacha-rh
      .sacha-rh-tabs
      button span {
        background:
          rgba(255, 255, 255, 0.7) !important;
        flex: 0 0 auto !important;
        font-size: 8px !important;
        margin: 0 !important;
        padding: 3px 5px !important;
      }
    }
  `;

  document.head.appendChild(
    tabsFitStyle
  );
}
/*
 * Matching pill tabs on every device.
 */
if (
  !document.getElementById(
    'sacha-rh-tabs-all-devices'
  )
) {
  const allDeviceTabStyle =
    document.createElement('style');

  allDeviceTabStyle.id =
    'sacha-rh-tabs-all-devices';

  allDeviceTabStyle.textContent = `
    .sacha-rh.sacha-rh
    .sacha-rh-tabs {
      background:
        var(--rh-beige) !important;
      border:
        1px solid
        var(--rh-gold) !important;
      border-radius: 30px !important;
      box-sizing: border-box !important;
      display: flex !important;
      gap: 0 !important;
      grid-template-columns: none !important;
      margin: 12px 0 18px !important;
      max-width: 520px !important;
      overflow: hidden !important;
      padding: 0 !important;
      width: min(100%, 520px) !important;
      justify-self: center !important;
left: 50% !important;
position: relative !important;
transform: translateX(-50%) !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs > button {
      align-items: center !important;
      background:
        transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-sizing: border-box !important;
      color: #111111 !important;
      cursor: pointer !important;
      display: flex !important;
      flex:
        1 1 0 !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      gap: 5px !important;
      justify-content: center !important;
      letter-spacing: 0.035em !important;
      margin: 0 !important;
      max-width: none !important;
      min-height: 46px !important;
      min-width: 0 !important;
      padding: 10px 6px !important;
      transform: none !important;
      white-space: normal !important;
      width: 0 !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs >
    button:first-child {
      border-radius:
        28px 0 0 28px !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs >
    button:last-child {
      border-radius:
        0 28px 28px 0 !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs >
    button.is-active {
      background:
        #c4a052 !important;
      color: #111111 !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs
    button span {
      background:
        rgba(
          255,
          255,
          255,
          0.7
        ) !important;
      border-radius: 10px !important;
      color: #111111 !important;
      flex: 0 0 auto !important;
      font-size: 8px !important;
      line-height: 1 !important;
      margin: 0 !important;
      padding: 3px 5px !important;
    }

    @media (max-width: 749px) {
      .sacha-rh.sacha-rh
      .sacha-rh-tabs {
        margin:
          0 0 16px !important;
      }

      .sacha-rh.sacha-rh
      .sacha-rh-tabs >
      button {
        font-size: 9.5px !important;
        gap: 4px !important;
        min-height: 44px !important;
        padding:
          9px 4px !important;
      }
    }
  `;

  document.head.appendChild(
    allDeviceTabStyle
  );
}
/*
 * Remove theme button overlays and
 * stabilise the tab layout.
 */
if (
  !document.getElementById(
    'sacha-rh-tab-overlay-fix'
  )
) {
  const tabOverlayFix =
    document.createElement('style');

  tabOverlayFix.id =
    'sacha-rh-tab-overlay-fix';

  tabOverlayFix.textContent = `
    .sacha-rh.sacha-rh
    .sacha-rh-tabs > button {
      box-shadow: none !important;
      flex: 1 1 0 !important;
      max-width: none !important;
      min-width: 0 !important;
      overflow: hidden !important;
      width: 0 !important;
    }

    .sacha-rh.sacha-rh
    .sacha-rh-tabs >
    button::before,
    .sacha-rh.sacha-rh
    .sacha-rh-tabs >
    button::after {
      box-shadow: none !important;
      content: none !important;
      display: none !important;
      height: 0 !important;
      inset: auto !important;
      position: static !important;
      width: 0 !important;
    }
[role="dialog"]:has(.sacha-rh) h1,
[role="dialog"]:has(.sacha-rh) h2,
[role="dialog"]:has(.sacha-rh) h3,
[role="dialog"]:has(.sacha-rh) [role="heading"],
[aria-modal="true"]:has(.sacha-rh) h1,
[aria-modal="true"]:has(.sacha-rh) h2,
[aria-modal="true"]:has(.sacha-rh) h3 {
  box-sizing: border-box !important;
  text-align: center !important;
  width: 100% !important;
}
    @media (min-width: 750px) {
      .sacha-rh.sacha-rh .sacha-rh-tabs {
  left: auto !important;
  margin: 12px auto 18px !important;
  max-width: 520px !important;
  position: relative !important;
  right: auto !important;
  transform: none !important;
  translate: none !important;
  width: 520px !important;
}
      }
@media (max-width: 749px) {
  .sacha-rh.sacha-rh
  .sacha-rh-tabs {
    box-sizing: border-box !important;
    display: block !important;
    height: 50px !important;
    left: auto !important;
    margin: 0 0 16px !important;
    max-width: 100% !important;
    min-height: 50px !important;
    overflow: hidden !important;
    position: relative !important;
    transform: none !important;
    translate: none !important;
    width: 100% !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-tabs > button {
    align-items: center !important;
    bottom: 0 !important;
    box-sizing: border-box !important;
    display: flex !important;
    flex: none !important;
    font-size: 11px !important;
    gap: 4px !important;
    justify-content: center !important;
    letter-spacing: 0 !important;
    line-height: 1.15 !important;
    margin: 0 !important;
    max-width: 50% !important;
    min-width: 50% !important;
    overflow: hidden !important;
    padding: 0 4px !important;
    position: absolute !important;
    top: 0 !important;
    transform: none !important;
    white-space: nowrap !important;
    width: 50% !important;
  }
.sacha-rh.sacha-rh
.sacha-rh-tabs > button {
  font-size: 0 !important;
  text-indent: 0 !important;
}

.sacha-rh.sacha-rh
.sacha-rh-tabs > button:first-of-type::after,
.sacha-rh.sacha-rh
.sacha-rh-tabs > button:last-of-type::after {
  align-items: center !important;
  box-sizing: border-box !important;
  color: #111111 !important;
  display: flex !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  height: 100% !important;
  inset: 0 !important;
  justify-content: center !important;
  letter-spacing: 0 !important;
  pointer-events: none !important;
  position: absolute !important;
  text-align: center !important;
  width: 100% !important;
}

.sacha-rh.sacha-rh
.sacha-rh-tabs > button:first-of-type::after {
  content: "VOUCHERS" !important;
}

.sacha-rh.sacha-rh
.sacha-rh-tabs > button:last-of-type::after {
  content: "POINTS & ACTIVITY" !important;
}
  .sacha-rh.sacha-rh
  .sacha-rh-tabs > button:first-of-type {
    left: 0 !important;
    right: auto !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-tabs > button:last-of-type {
    left: 50% !important;
    right: auto !important;
  }

  .sacha-rh.sacha-rh
  .sacha-rh-tabs button span {
    display: none !important;
    flex: 0 0 auto !important;
    font-size: 8px !important;
  }
}
  .sacha-rh.sacha-rh
.sacha-rh-tabs button span {
  display: none !important;
}
  `;

  document.head.appendChild(
    tabOverlayFix
  );
}
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      render,
      { once: true }
    );
  } else {
    render();
  }

  new MutationObserver(() => {
    window.clearTimeout(timer);

    timer = window.setTimeout(
      render,
      100
    );
  }).observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();